/**
 * Middleware to manage critical field dependencies in the appointment booking flow
 *
 * This middleware monitors changes to critical fields like vehicle details,
 * shop location, and appointment type, automatically resetting dependent
 * selections (services, time slots) when these fields change to prevent
 * inconsistent booking states.
 *
 * Instead of blocking with confirmation dialogs, it automatically resets
 * dependent fields and dispatches custom events for the UI to show
 * notifications with undo options.
 */

import {
    clearSelectedOption,
    setSelectedServices,
} from '@/lib/store/features/booking/bookingSlice';
import { RootState } from '@/lib/store/store';
import { ServiceItem } from '@/lib/types/operations/service';
import { Middleware, MiddlewareAPI } from '@reduxjs/toolkit';

// Define action type interface
interface TypedAction {
  type: string;
  [key: string]: any;
}

// Action types that should trigger service reset
const SERVICE_RESET_ACTIONS = [
  // Removed 'booking/setVehicleDetails' - vehicle changes should not reset services
  // Vehicle type changes should only trigger pricing updates, not service resets
  'booking/setShopLocation'
]

// Keep track of the last state for potential undo operations
let lastServiceIds: string[] = []

// Type guard to check if an action is a TypedAction
function isTypedAction(action: unknown): action is TypedAction {
  return typeof action === 'object' &&
         action !== null &&
         'type' in action &&
         typeof (action as any).type === 'string';
}

// Helper to check if services are compatible with the new vehicle/location
function areServicesCompatible(
  services: string[],
  state: RootState,
  actionType: string,
  action: TypedAction
): boolean {
  // If no services selected, they're always compatible
  if (!services.length) return true;

  const currentServices = state.services.items as ServiceItem[];

  // For location changes, check if all services are available at the new location
  if (actionType.includes('Location')) {
    const locationId = action.payload;
    if (!locationId) return true; // If we can't determine location, assume compatible

    // CRITICAL FIX: If no services are loaded yet, we can't determine compatibility
    // This happens when location changes before services are fetched for the new location
    // In this case, we should assume compatibility and let the ServicesStep handle validation
    if (!currentServices.length) {
      console.log('[criticalFieldResetMiddleware] No services loaded yet, assuming compatibility for location change');
      return true;
    }

    // Check if services were loaded for the current location being changed to
    const servicesLastLoadedLocationId = state.services.lastLoadedLocationId;
    if (servicesLastLoadedLocationId !== locationId) {
      console.log('[criticalFieldResetMiddleware] Services not loaded for target location, assuming compatibility');
      return true;
    }

    // Now we can safely check compatibility since services are loaded for this location
    return services.every(serviceId => {
      const service = currentServices.find((s: ServiceItem) => s.id === serviceId);
      if (!service) {
        console.warn('[criticalFieldResetMiddleware] Service not found in loaded services:', serviceId);
        return false;
      }
      return service?.shopLocations?.some(location => location.id === locationId);
    });
  }

  return true;
}

// Helper to update search params without services
function clearServicesFromSearchParams(): void {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  const searchParams = url.searchParams;

  // Remove all service parameters while keeping others
  const newParams = new URLSearchParams();
  searchParams.forEach((value, key) => {
    // Keep everything except services
    if (key !== 'services') {
      newParams.append(key, value);
    }
  });

  // Update the URL without triggering a page reload
  const newUrl = `${window.location.pathname}${newParams.toString() ? '?' + newParams.toString() : ''}`;
  window.history.replaceState({}, '', newUrl);
}

export const criticalFieldResetMiddleware: Middleware =
  (api: MiddlewareAPI) =>
  (next) =>
  (action) => {
    const { dispatch, getState } = api
    const prevState = getState() as RootState
    const prevServices = prevState.booking.persisted.selectedServiceIds || []
    const hadOption = prevState.booking.persisted.selectedOption !== null

    // Keep backups for potential undo operations
    lastServiceIds = [...prevServices]

    // Allow the action to be processed first
    const result = next(action)

    // Get the updated state
    const currentState = getState() as RootState

    // NOTE: Vehicle type changes (booking/setVehicleDetails) do NOT reset services
    // Vehicle type changes should trigger service pricing updates in the ServicesStep component
    // or through a separate pricing middleware/effect

    // Safe type check for action
    if (isTypedAction(action)) {
      const actionType = action.type

      // Only handle service resets for location changes
      if (SERVICE_RESET_ACTIONS.includes(actionType)) {
        const isLocationChange = actionType === 'booking/setShopLocation'

        // Only reset if we have selected services AND they're incompatible with the new location
        if (prevServices.length > 0 && isLocationChange) {
          console.log('[criticalFieldResetMiddleware] Checking service compatibility for location change:', {
            actionType,
            prevServicesCount: prevServices.length,
            currentServicesCount: currentState.services.items.length,
            lastLoadedLocationId: currentState.services.lastLoadedLocationId,
            newLocationId: action.payload
          });

          const shouldReset = !areServicesCompatible(prevServices, currentState, actionType, action)

          console.log('[criticalFieldResetMiddleware] Location compatibility check result:', {
            shouldReset,
            newLocationId: action.payload,
            reason: shouldReset ? 'Services incompatible with new location - will reset' : 'Services compatible with new location - no reset needed'
          });

          if (shouldReset) {
            // Clear selected services
            dispatch(setSelectedServices([]))

            // Also clear selected option since services are reset
            if (hadOption) {
              dispatch(clearSelectedOption())
            }

            // Clear services from search params
            clearServicesFromSearchParams();

            // Only notify if user has to repeat a step
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('appointmentServicesReset', {
                detail: {
                  reason: actionType,
                  previousServices: prevServices.length,
                  forceRepeat: true
                }
              }))
            }
          }
        }
      }
      // Handle selected-option reset for service duration changes
      else if (actionType === 'booking/setSelectedServices' && hadOption) {
        const currentServices = currentState.booking.persisted.selectedServiceIds || []

        // Skip if this is a service reset action
        if (currentServices.length === 0 && prevServices.length > 0) {
          return result
        }

        // Calculate service durations
        const prevDuration = prevServices.reduce((total: number, serviceId: string) => {
          const service = currentState.services.items.find((s: ServiceItem) => s.id === serviceId);
          return total + (Number(service?.labourHours) || 0);
        }, 0);

        const currentDuration = currentServices.reduce((total: number, serviceId: string) => {
          const service = currentState.services.items.find((s: ServiceItem) => s.id === serviceId);
          return total + (Number(service?.labourHours) || 0);
        }, 0);

        // Only reset selected option if duration changed significantly
        if (Math.abs(prevDuration - currentDuration) > 0.5) {
          dispatch(clearSelectedOption())

          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('appointmentTimeSlotsReset', {
              detail: {
                reason: 'serviceDurationChanged',
                previousDuration: prevDuration,
                newDuration: currentDuration,
                forceRepeat: true
              }
            }))
          }
        }
      }
    }

    return result
  }

// Expose functions to restore previous state (for undo operations)
export const restoreServices = () => {
  return (dispatch: any) => {
    if (lastServiceIds.length > 0) {
      dispatch(setSelectedServices(lastServiceIds))
    }
  }
}
